import { useMemo } from 'react';

import useInfiniteScroll from 'react-infinite-scroll-hook';

import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerProps,
  Typography,
} from '@cast/design-system';

import { ResizeEventsTable } from '../_components/ResizeEventsTable';
import {
  STORAGE_ACTIONS_PAGE_SIZE,
  useStorageActionsQuery,
} from 'hooks/queries/storage-autoscaler';

import { useNodeStorageTimeRangeSelection } from '../_hooks/useNodeStorageTimeRangeSelection';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import { FailedToLoad } from '@cast/shared-messages-ui';

type AllActivityDrawerProps = DrawerProps & {
  nodeName?: string;
};

export const AllActivityDrawer = ({
  nodeName,
  ...drawerProps
}: AllActivityDrawerProps) => {
  const { cluster } = useCluster();
  const { getRange, key } = useNodeStorageTimeRangeSelection();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { from, to } = useMemo(() => getRange(), [key]);
  const {
    storageActions,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
  } = useStorageActionsQuery({
    clusterId: cluster.id,
    nodeName,
    startTime: from,
    endTime: to,
    pageSize: STORAGE_ACTIONS_PAGE_SIZE,
  });

  const [infiniteRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage,
    disabled: !!error,
    onLoadMore: fetchNextPage,
  });

  return (
    <Drawer testId="all-activity-drawer" size="medium" {...drawerProps}>
      <DrawerHeader />

      <DrawerBody>
        <DrawerContent px={0}>
          <Typography variant="h5" sx={{ mb: 16, px: 32 }}>
            All activity
          </Typography>

          {error ? (
            <FailedToLoad title={error.message} refresh={refetch} />
          ) : (
            <>
              <ResizeEventsTable
                hideFirstAndLastColumnPaddings
                storageActions={storageActions}
                isLoading={isLoading}
                isFetchingNextPage={isFetchingNextPage}
              />

              {/* Infinite scroll sentinel */}
              <div ref={infiniteRef} />
            </>
          )}
        </DrawerContent>
      </DrawerBody>
    </Drawer>
  );
};
